// src/features/landing/presentation/pages/AgentJobBoardPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { useAuthContext } from "../../../../features/auth/application/AuthContext";
import { jobSeekerRequestRepository } from "../../infrastructure/jobSeekerRequestApiRepository";
import { recruitmentRequestRepository } from "../../infrastructure/recruitmentRequestApiRepository";
import { jobCategoryRepository } from "../../infrastructure/jobCategoryApiRepository";
import { RECRUIT_REGIONS } from "../../domain/recruitmentOptions";

const TABS = [
    { key: "all",    label: "전체" },
    { key: "seeker", label: "구직" },
    { key: "hire",   label: "구인" },
];

const APPLICANT_TYPE_LABEL = {
    AGENCY_CEO: "대리점 대표",
    HR_MANAGER: "채용 담당자",
};

const EXPERIENCE_LABEL = {
    "신입":     "신입",
    "1년미만":  "1년 미만",
    "1~3년":   "1~3년",
    "3~5년":   "3~5년",
    "5~10년":  "5~10년",
    "10년이상": "10년 이상",
};

function extractRegion(address) {
    if (!address) return '';
    const parts = address.trim().split(/\s+/);
    const city = parts[0]
        .replace('특별시', '').replace('광역시', '').replace('특별자치시', '')
        .replace('특별자치도', '').replace('도', '').trim();
    const district = parts[1] || '';
    return district ? `${city} ${district}` : city;
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    return String(dateStr).slice(0, 10);
}

const ALL_REGION_OPTION = { code: "", label: "전체" };

// 네이티브 <select>는 옵션 목록이 OS 기본 폰트로 렌더링돼 사이트 폰트와 어긋나므로,
// 직접 그리는 커스텀 드롭다운으로 대체한다.
function RegionSelect({ value, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const options = [ALL_REGION_OPTION, ...RECRUIT_REGIONS];
    const selected = options.find((o) => o.code === value) || ALL_REGION_OPTION;

    return (
        <div className="ajb-region-select" ref={wrapRef}>
            <button
                type="button"
                className={`ajb-region-trigger${isOpen ? " ajb-region-trigger--open" : ""}`}
                onClick={() => setIsOpen((v) => !v)}
            >
                <span className={value ? "" : "ajb-region-trigger-placeholder"}>{selected.label}</span>
                <svg className="ajb-region-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>
            {isOpen && (
                <div className="ajb-region-dropdown" role="listbox">
                    {options.map((o) => (
                        <div
                            key={o.code || "all"}
                            role="option"
                            aria-selected={o.code === value}
                            className={`ajb-region-option${o.code === value ? " ajb-region-option--selected" : ""}`}
                            onClick={() => { onChange(o.code); setIsOpen(false); }}
                        >
                            <span>{o.label}</span>
                            {o.code === value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function AgentJobBoardPage() {
    const navigate = useNavigate();
    const { isLoggedIn, member } = useAuthContext();
    const role = member?.role;
    const showSeeker = !isLoggedIn || role === 'JOB_SEEKER';
    const showHire   = !isLoggedIn || role === 'COMPANY';

    const [activeTab, setActiveTab] = useState("all");
    const [region, setRegion] = useState("");
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const [catData, seekerData, hireData] = await Promise.all([
                    jobCategoryRepository.getJobCategories(),
                    jobSeekerRequestRepository.getList({ jobCategoryCode: 'INSURANCE_PLANNER', size: 100 }),
                    recruitmentRequestRepository.getList({ jobCategoryCode: 'INSURANCE_PLANNER', size: 100 }),
                ]);

                // code → name 맵
                const map = {};
                catData.forEach((c) => { map[c.code] = c.name; });

                // 구직 목록 변환 (DONE·CANCEL 제외)
                const seekerPosts = (seekerData.content || [])
                    .filter((r) => r.status !== 'DONE' && r.status !== 'CANCEL' && r.isActive !== false)
                    .map((r) => {
                        const cats = (r.jobCategoryCodes || []).map((c) => map[c] || c);
                        const expLabel = EXPERIENCE_LABEL[r.jobExperienceLevel] || r.jobExperienceLevel || '';
                        const title = cats.length > 0
                            ? `${cats.slice(0, 2).join('·')} 경력 ${expLabel} 구직합니다`
                            : `경력 ${expLabel} 보험 설계사 구직합니다`;
                        return {
                            _key: `s-${r.id}`,
                            _id: r.id,
                            type: 'seeker',
                            title,
                            region: extractRegion(r.address),
                            tag: expLabel || '경력 무관',
                            cats: cats.slice(0, 3),
                            dateStr: formatDate(r.createdAt),
                            _ts: r.createdAt || '',
                        };
                    });

                // 구인 목록 변환 (COMPLETED·REJECTED 제외)
                const hirePosts = (hireData.content || [])
                    .filter((r) => r.status !== 'COMPLETED' && r.status !== 'REJECTED' && r.isActive !== false)
                    .map((r) => {
                        const cats = (r.jobCategoryCodes || []).map((c) => map[c] || c);
                        const typeLabel = APPLICANT_TYPE_LABEL[r.applicantType] || r.applicantType || '';
                        const title = r.companyName
                            ? `${r.companyName} 설계사 모집`
                            : `설계사 모집 (${typeLabel})`;
                        return {
                            _key: `h-${r.id}`,
                            _id: r.id,
                            type: 'hire',
                            title,
                            region: extractRegion(r.companyAddress),
                            recruitRegions: r.recruitRegions || [],
                            tag: typeLabel,
                            cats: cats.slice(0, 3),
                            applicationCount: r.applicationCount ?? 0,
                            dateStr: formatDate(r.createdDate || r.createdAt),
                            _ts: r.createdAt || r.createdDate || '',
                        };
                    });

                // 최신순 정렬
                const combined = [...seekerPosts, ...hirePosts].sort(
                    (a, b) => (b._ts > a._ts ? 1 : b._ts < a._ts ? -1 : 0)
                );
                setPosts(combined);
            } catch (e) {
                setError(e.message || '데이터를 불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    const byTab = activeTab === "all"
        ? posts
        : posts.filter((p) => p.type === activeTab);

    // 지역 필터는 구인 탭에서만 노출·적용한다 (recruitRegions = 채용 지역 조인 테이블).
    // '전국(NATIONWIDE)' 구인 글은 지역 필터와 무관하게 항상 노출한다.
    const filtered = activeTab === "hire" && region
        ? byTab.filter((p) =>
            p.recruitRegions.includes(region) ||
            p.recruitRegions.includes("NATIONWIDE"))
        : byTab;

    return (
        <>
            <div className="ajb-page">

                {/* 히어로 */}
                <div className="ajb-hero">
                    <div className="ajb-hero-inner">
                        <p className="ajb-hero-kicker">PREMIUM · 설계사 커뮤니티</p>
                        <h1 className="ajb-hero-title">설계사 구인/구직</h1>
                        <p className="ajb-hero-sub">보험 설계사와 채용 기업을 직접 연결하는 전문 게시판입니다.</p>
                        <div className="ajb-hero-actions">
                            {showHire && (
                                <Link to="/premium/company/planner/rep" className="ajb-hero-btn ajb-hero-btn--primary">채용등록</Link>
                            )}
                            {showSeeker && (
                                <Link to="/premium/apply/planner" className="ajb-hero-btn ajb-hero-btn--outline">
                                    인재등록
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                <div className="ajb-body">

                    {/* 탭 */}
                    <div className="ajb-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                className={`ajb-tab${activeTab === t.key ? " ajb-tab--active" : ""}`}
                                onClick={() => setActiveTab(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                        {!loading && (
                            <span className="ajb-tab-count">{filtered.length}건</span>
                        )}
                    </div>

                    {/* 지역 필터 (구인 글 대상, 구인 탭에서만 노출) */}
                    {activeTab === "hire" && (
                        <div className="ajb-filter">
                            <span className="ajb-filter-label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                </svg>
                                채용 지역
                            </span>
                            <RegionSelect value={region} onChange={setRegion} />
                            <button
                                type="button"
                                className="ajb-filter-clear"
                                onClick={() => setRegion("")}
                                disabled={!region}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                                </svg>
                                초기화
                            </button>
                        </div>
                    )}

                    {/* 로딩 */}
                    {loading && (
                        <div className="ajb-state">
                            <div className="ajb-spinner" />
                            <span>불러오는 중...</span>
                        </div>
                    )}

                    {/* 에러 */}
                    {!loading && error && (
                        <div className="ajb-state ajb-state--error">{error}</div>
                    )}

                    {/* 빈 목록 */}
                    {!loading && !error && filtered.length === 0 && (
                        <div className="ajb-state">
                            <span>등록된 게시글이 없습니다.</span>
                        </div>
                    )}

                    {/* 게시글 목록 */}
                    {!loading && !error && filtered.length > 0 && (
                        <div className="ajb-list">
                            {filtered.map((post) => (
                                <div
                                    key={post._key}
                                    className="ajb-card"
                                    onClick={() => navigate(`/premium/job-board/${post.type}/${post._id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="ajb-card-top">
                                        <span className={`ajb-badge ajb-badge--${post.type}`}>
                                            {post.type === "seeker" ? "구직" : "구인"}
                                        </span>
                                        <span className="ajb-tag">{post.tag}</span>
                                    </div>
                                    <div className="ajb-card-title">{post.title}</div>
                                    <div className="ajb-card-meta">
                                        {post.region && (
                                            <span className="ajb-meta-item">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                                </svg>
                                                {post.region}
                                            </span>
                                        )}
                                        {post.cats.length > 0 && (
                                            <span className="ajb-meta-item">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                                                </svg>
                                                {post.cats.join(' · ')}
                                            </span>
                                        )}
                                        {post.type === "hire" && (
                                            <span className="ajb-meta-item">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                                지원 {post.applicationCount}명
                                            </span>
                                        )}
                                        <span className="ajb-meta-date">{post.dateStr}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 하단 CTA */}
                    <div className="ajb-cta-row">
                        {showSeeker && (
                            <div className="ajb-cta-card">
                                <div className="ajb-cta-icon">🔍</div>
                                <div className="ajb-cta-title">설계사로 활동하고 싶으신가요?</div>
                                <div className="ajb-cta-desc">인재등록 폼을 통해 담당 헤드헌터에게 직접 연결됩니다.</div>
                                <Link to="/premium/apply/planner" className="ajb-cta-btn">인재등록 →</Link>
                            </div>
                        )}
                        {showHire && (
                            <div className="ajb-cta-card">
                                <div className="ajb-cta-icon">🏢</div>
                                <div className="ajb-cta-title">설계사를 채용하고 싶으신가요?</div>
                                <div className="ajb-cta-desc">채용 신청서를 등록하시면 조건에 맞는 인재를 소개해 드립니다.</div>
                                <Link to="/premium/company/planner/rep" className="ajb-cta-btn">채용등록 →</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />

            <style>{`
                .ajb-page { padding-top: 40px; min-height: 100vh; background: #f7f4f1; }

                .ajb-hero {
                    background: linear-gradient(135deg, #fd7314 0%, #f1863f 100%);
                    padding: 52px 20px; text-align: center;
                }
                .ajb-hero-inner { max-width: 700px; margin: 0 auto; }
                .ajb-hero-kicker { font-size: 12px; letter-spacing: 0.2em; color: rgba(255,255,255,0.8); font-weight: 600; margin: 0 0 8px; }
                .ajb-hero-title { font-size: 2rem; font-weight: 800; color: #fff; margin: 0 0 10px; }
                .ajb-hero-sub { font-size: 1rem; color: rgba(255,255,255,0.85); margin: 0 0 24px; }
                .ajb-hero-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
                .ajb-hero-btn {
                    display: inline-flex; align-items: center; padding: 11px 24px;
                    border-radius: 8px; font-size: 0.95rem; font-weight: 700;
                    cursor: pointer; text-decoration: none; transition: all 0.2s;
                }
                .ajb-hero-btn--primary { background: #fff; color: #f16102; border: none; }
                .ajb-hero-btn--primary:hover { background: #fff3e0; }
                .ajb-hero-btn--outline { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.7); }
                .ajb-hero-btn--outline:hover { background: rgba(255,255,255,0.15); border-color: #fff; }

                .ajb-body { max-width: 900px; margin: 0 auto; padding: 40px 20px 80px; }

                .ajb-tabs {
                    display: flex; align-items: center; gap: 6px;
                    background: #fff; border-radius: 12px; padding: 6px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.07); margin-bottom: 20px;
                }
                .ajb-tab {
                    padding: 9px 22px; border: none; border-radius: 8px;
                    font-size: 0.9rem; font-weight: 600; color: #888;
                    background: transparent; cursor: pointer; transition: all 0.18s;
                }
                .ajb-tab:hover { color: #f16102; background: #fff8f4; }
                .ajb-tab--active { background: #f16102; color: #fff; }
                .ajb-tab-count { margin-left: auto; font-size: 0.82rem; color: #aaa; font-weight: 500; padding-right: 8px; }

                /* 지역 필터 */
                .ajb-filter {
                    display: flex; align-items: center; gap: 12px;
                    margin-bottom: 20px; padding: 12px 16px;
                    background: #fff; border-radius: 12px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                }
                .ajb-filter-label {
                    display: inline-flex; align-items: center; gap: 5px;
                    font-size: 0.82rem; font-weight: 600; color: #888; flex-shrink: 0;
                }
                .ajb-filter-label svg { color: #cbbfb4; }
                /* 커스텀 지역 드롭다운 (네이티브 select의 OS 폰트 불일치 문제를 피하기 위해 직접 렌더링) */
                .ajb-region-select { position: relative; min-width: 170px; }
                .ajb-region-trigger {
                    width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px;
                    padding: 8px 12px 8px 14px; border: 1.5px solid #e5ddd7; border-radius: 8px;
                    font-family: inherit; font-size: 0.85rem; color: #1c1814; background: #fdfbf9; cursor: pointer;
                    transition: border-color 0.18s, background 0.18s;
                }
                .ajb-region-trigger:hover { border-color: #f0c9a6; background: #fff; }
                .ajb-region-trigger--open { border-color: #f16102; background: #fff; }
                .ajb-region-trigger-placeholder { color: #a39990; }
                .ajb-region-chevron { flex-shrink: 0; color: #b3a89e; transition: color 0.18s, transform 0.18s; }
                .ajb-region-trigger--open .ajb-region-chevron { color: #f16102; transform: rotate(180deg); }
                .ajb-region-dropdown {
                    position: absolute; top: calc(100% + 6px); left: 0; right: 0;
                    background: #fff; border: 1px solid #eee2d8; border-radius: 10px;
                    box-shadow: 0 8px 24px rgba(28,24,20,0.12); z-index: 40;
                    max-height: 280px; overflow-y: auto; padding: 6px;
                    animation: ajb-dropdown-in 0.16s ease;
                }
                @keyframes ajb-dropdown-in {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .ajb-region-option {
                    display: flex; align-items: center; justify-content: space-between; gap: 8px;
                    padding: 9px 12px; border-radius: 7px;
                    font-family: inherit; font-size: 0.85rem; color: #3a332c; cursor: pointer;
                    transition: background 0.12s, color 0.12s;
                }
                .ajb-region-option:hover { background: #fff3e0; color: #f16102; }
                .ajb-region-option--selected { background: #fff3e0; color: #f16102; font-weight: 700; }
                .ajb-region-option svg { flex-shrink: 0; color: #f16102; }
                .ajb-filter-clear {
                    display: inline-flex; align-items: center; gap: 5px;
                    background: #fff3e0; border: 1.5px solid transparent; border-radius: 8px;
                    font-size: 0.8rem; color: #f16102; cursor: pointer; font-weight: 700;
                    padding: 8px 13px; transition: background 0.18s, border-color 0.18s, opacity 0.18s;
                    flex-shrink: 0;
                }
                .ajb-filter-clear:hover:not(:disabled) { background: #ffe6c8; border-color: #fbd2a3; }
                .ajb-filter-clear:disabled {
                    background: transparent; color: #c2b9b0; cursor: not-allowed; opacity: 0.6;
                }
                .ajb-filter-clear svg { flex-shrink: 0; }

                /* 상태 메시지 */
                .ajb-state {
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    gap: 12px; padding: 60px 20px; color: #aaa; font-size: 0.9rem;
                }
                .ajb-state--error { color: #e53935; }
                .ajb-spinner {
                    width: 28px; height: 28px; border: 3px solid #fde0c8;
                    border-top-color: #f16102; border-radius: 50%;
                    animation: ajb-spin 0.7s linear infinite;
                }
                @keyframes ajb-spin { to { transform: rotate(360deg); } }

                /* 게시글 목록 */
                .ajb-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 40px; }
                .ajb-card {
                    background: #fff; border-radius: 12px; padding: 18px 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    transition: box-shadow 0.18s, transform 0.18s;
                }
                .ajb-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.10); transform: translateY(-2px); }
                .ajb-card-top { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
                .ajb-badge {
                    display: inline-flex; align-items: center; padding: 3px 10px;
                    border-radius: 20px; font-size: 0.75rem; font-weight: 700;
                }
                .ajb-badge--seeker { background: #e8f4fd; color: #1976d2; }
                .ajb-badge--hire   { background: #fff3e0; color: #f16102; }
                .ajb-tag { font-size: 0.75rem; color: #999; background: #f5f5f5; padding: 3px 9px; border-radius: 20px; }
                .ajb-card-title { font-size: 0.97rem; font-weight: 700; color: #1c1814; margin-bottom: 10px; }
                .ajb-card-meta { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
                .ajb-meta-item { display: flex; align-items: center; gap: 4px; font-size: 0.82rem; color: #888; }
                .ajb-meta-date { margin-left: auto; font-size: 0.8rem; color: #bbb; }

                /* 하단 CTA */
                .ajb-cta-row { display: flex; justify-content: center; gap: 16px; }
                .ajb-cta-card { flex: 1; max-width: 420px; }
                .ajb-cta-card {
                    background: #fff; border-radius: 16px; padding: 28px 24px;
                    text-align: center; box-shadow: 0 2px 10px rgba(0,0,0,0.07);
                }
                .ajb-cta-icon { font-size: 2rem; margin-bottom: 10px; }
                .ajb-cta-title { font-size: 1rem; font-weight: 700; color: #1c1814; margin-bottom: 8px; }
                .ajb-cta-desc { font-size: 0.85rem; color: #888; line-height: 1.6; margin-bottom: 18px; }
                .ajb-cta-btn {
                    display: inline-flex; align-items: center; padding: 10px 22px;
                    background: #f16102; color: #fff; border-radius: 8px;
                    font-size: 0.88rem; font-weight: 700; text-decoration: none;
                    transition: background 0.2s;
                }
                .ajb-cta-btn:hover { background: #d95502; }

                @media (max-width: 640px) {
                    .ajb-hero { padding: 36px 16px; }
                    .ajb-hero-title { font-size: 1.5rem; }
                    .ajb-body { padding: 24px 14px 56px; }
                    .ajb-cta-row { grid-template-columns: 1fr; }
                    .ajb-meta-date { margin-left: 0; }
                    .ajb-card { padding: 16px; }
                    .ajb-filter { flex-wrap: wrap; }
                    .ajb-region-select { flex: 1; width: 100%; min-width: 0; }
                }
            `}</style>
        </>
    );
}
